import { IAddJobPayload } from "@endpoints/job.service";
import {
  EMPTY_SKILL,
  JOB_FORM_DEFAULTS,
  JobFormValues,
  parseAmount,
  splitLines,
} from "../schema/jobForm.schema";

const withoutUndefined = <T extends object>(object: T) =>
  Object.fromEntries(
    Object.entries(object).filter(([, value]) => value !== undefined),
  ) as Partial<T>;

const toOptionalAmount = (value: string) =>
  value?.trim() && !Number.isNaN(parseAmount(value))
    ? parseAmount(value)
    : undefined;

const pad = (value: number) => String(value).padStart(2, "0");

// Form values -> job API payload. Sends the whole draft so every save is
// idempotent; skills and payment are not part of this endpoint.
export const toJobPayload = (
  values: JobFormValues,
): Partial<IAddJobPayload> => {
  const salaryMin = toOptionalAmount(values.salaryMin);
  const salaryMax = toOptionalAmount(values.salaryMax);
  const isScheduled =
    values.publicationType === 'Schedule_Publication' &&
    values.publicationDate &&
    values.publicationTime;

  return withoutUndefined({
    creationStrategy: values.creationStrategy,
    jobRequirementId:
      values.creationStrategy === 'Requirement_Import'
        ? values.jobRequirementId || undefined
        : undefined,
    companyId: values.companyId || undefined,
    showCompanyDetails: values.showCompanyDetails,
    classification: values.classification
      ? {
          main: values.classification,
          sub: values.discipline ? [values.discipline] : [],
        }
      : undefined,
    title: values.title,
    positionLevel: values.positionLevel ?? undefined,
    location: values.location,
    workPlaceOption: values.workPlaceOption ?? undefined,
    jobType: values.jobType ?? undefined,
    jobPostDurationDays: values.jobPostDurationDays ?? undefined,
    qualificationEligibility: values.qualificationEligibility,
    compensationType: values.compensationType,
    currency: values.currency,
    salary:
      salaryMin !== undefined || salaryMax !== undefined
        ? { min: salaryMin, max: salaryMax }
        : undefined,
    jobOverview: values.jobOverview,
    keyResponsibilities: splitLines(values.keyResponsibilities),
    industryType: values.industryType,
    salaryAndBenefits: values.salaryAndBenefits,
    matchVisibilityMode: values.matchVisibilityMode,
    reviewThreshold: values.reviewThreshold,
    belowThresholdHandling: values.belowThresholdHandling,
    queueHealthNotificationEnabled: values.queueHealthNotificationEnabled,
    queueHealthMinimum: values.queueHealthMinimum,
    publicationType: values.publicationType,
    publishedDate: isScheduled
      ? new Date(
          `${values.publicationDate}T${values.publicationTime}`,
        ).toISOString()
      : undefined,
    publicationTimezone: values.publicationTimezone,
  });
};

// Job API response -> form values, used when resuming a saved draft.
export const fromJob = (job: any): JobFormValues => {
  const published = job?.publishedDate ? new Date(job.publishedDate) : null;

  return {
    ...JOB_FORM_DEFAULTS,
    creationStrategy:
      job?.creationStrategy ?? (job?.jobRequirementId ? "import" : "manual"),
    jobRequirementId: job?.jobRequirementId ?? "",
    requirementRefNo: job?.jobRequirement?.refNo ?? "",
    companyId: job?.companyId ?? job?.company?._id ?? "",
    companyName: job?.company?.name ?? job?.company?.companyName ?? "",
    showCompanyDetails:
      job?.showCompanyDetails ?? JOB_FORM_DEFAULTS.showCompanyDetails,
    classification: job?.classification?.main ?? "",
    discipline: job?.classification?.sub?.[0] ?? "",
    title: job?.title ?? "",
    positionLevel: job?.positionLevel ?? null,
    location: job?.location ?? "",
    workPlaceOption: job?.workPlaceOption ?? null,
    jobType: job?.jobType ?? null,
    jobPostDurationDays: job?.jobPostDurationDays ?? null,
    qualificationEligibility: job?.qualificationEligibility ?? "",
    compensationType:
      job?.compensationType ?? JOB_FORM_DEFAULTS.compensationType,
    currency: job?.currency ?? JOB_FORM_DEFAULTS.currency,
    salaryMin: job?.salary?.min != null ? String(job.salary.min) : "",
    salaryMax: job?.salary?.max != null ? String(job.salary.max) : "",
    jobOverview: job?.jobOverview ?? "",
    keyResponsibilities: (job?.keyResponsibilities ?? []).join("\n"),
    industryType: job?.industryType ?? "",
    salaryAndBenefits: job?.salaryAndBenefits ?? "",
    matchVisibilityMode:
      job?.matchVisibilityMode ?? JOB_FORM_DEFAULTS.matchVisibilityMode,
    reviewThreshold: job?.reviewThreshold ?? JOB_FORM_DEFAULTS.reviewThreshold,
    belowThresholdHandling:
      job?.belowThresholdHandling ?? JOB_FORM_DEFAULTS.belowThresholdHandling,
    queueHealthNotificationEnabled:
      job?.queueHealthNotificationEnabled ??
      JOB_FORM_DEFAULTS.queueHealthNotificationEnabled,
    queueHealthMinimum:
      job?.queueHealthMinimum ?? JOB_FORM_DEFAULTS.queueHealthMinimum,
    publicationType: job?.publicationType ?? JOB_FORM_DEFAULTS.publicationType,
    publicationDate: published
      ? `${published.getFullYear()}-${pad(published.getMonth() + 1)}-${pad(published.getDate())}`
      : "",
    publicationTime: published
      ? `${pad(published.getHours())}:${pad(published.getMinutes())}`
      : "",
    publicationTimezone:
      job?.publicationTimezone ?? JOB_FORM_DEFAULTS.publicationTimezone,
  };
};

// Approved hiring-company requirement -> the fields it can prefill.
export const fromRequirement = (requirement: any): Partial<JobFormValues> => ({
  jobRequirementId: requirement?._id ?? "",
  requirementRefNo: requirement?.refNo ?? "",
  companyId: requirement?.companyId ?? requirement?.company?._id ?? "",
  companyName: requirement?.company?.name ?? "",
  title: requirement?.title ?? "",
  location: requirement?.location ?? "",
  workPlaceOption: requirement?.workPlaceOption ?? null,
  jobType: requirement?.jobType ?? null,
  industryType: requirement?.industryType ?? "",
  jobOverview: requirement?.description ?? "",
  salaryMin:
    requirement?.salary?.min != null ? String(requirement.salary.min) : "",
  salaryMax:
    requirement?.salary?.max != null ? String(requirement.salary.max) : "",
  skills: requirement?.skills?.length
    ? requirement.skills.map((skill) => ({ ...EMPTY_SKILL, title: skill.title }))
    : [{ ...EMPTY_SKILL }],
});
